import createClient from "openapi-fetch";
import type { paths } from "./schema";

const STRAPI_URL =
  process.env.NEXT_PUBLIC_STRAPI_BASE_URL || "http://localhost:1337";

/**
 * Strapi's REST query language in the shape `openapi-fetch` can serialise.
 *
 * Two things the generated spec does not describe, both because
 * `@strapi/plugin-documentation` writes the parameter list by hand rather than from the
 * query engine:
 *
 * - `populate` is typed `string`, but anything past a flat relation needs bracket notation
 *   (`populate[contentSectionsV2][on][sections-v2.faq-accordion][populate][faqs]=*`), which
 *   is an object here and a path there.
 * - `publicationState` is missing entirely, so drafts could not be requested at all.
 *
 * Widening it here keeps the cast in one place. Everything on the response side stays
 * generated, which is the half worth generating.
 */
export type StrapiQuery = Record<string, unknown>;

/**
 * Bracket notation, recursively — Strapi reads `qs`, not OpenAPI's `form` style. `deepObject`
 * is the one serialiser that produces `a[b][c]=v`, and `*` must survive unencoded because
 * Strapi treats `%2A` as a field name.
 */
const serialize = (query: StrapiQuery, prefix = ""): string =>
  Object.entries(query)
    .filter(([, value]) => value !== undefined)
    .flatMap(([key, value]) => {
      const path = prefix ? `${prefix}[${key}]` : key;
      if (value !== null && typeof value === "object") {
        return [serialize(value as StrapiQuery, path)];
      }
      return [
        `${encodeURIComponent(path).replace(/%5B/g, "[").replace(/%5D/g, "]")}=${encodeURIComponent(String(value))}`,
      ];
    })
    .filter(Boolean)
    .join("&");

const strapi = createClient<paths>({
  baseUrl: `${STRAPI_URL}/api`,
  querySerializer: (query) => serialize(query as StrapiQuery),
});

export default strapi;
